import {
    type CSSProperties,
    type ReactNode,
    Fragment,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    type ISiteLogoProps,
    NCoreUIKitTheme,
    PageContainer,
    MainHeader,
    Button,
    Switch,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type DocsStyleTokens,
    BulletList,
    Paragraph,
    CodeBlock,
    DemoFrame,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    SearchIcon,
    BellIcon
} from "../../anatomy/icons";
import ncoreLogo from "../../assets/ncore-logo.png";

const NAME = "MainHeader";

const USAGE_CODE = `<MainHeader
    siteLogoProps={{
        onPress: () => navigation.navigate("Home"),
        isWorkWithAction: true,
        subTitle: "Design System",
        title: "NCore"
    }}
    renderRight={() => <Button
        onPress={openSignIn}
        title="Sign in"
    />}
    renderCenter={() => <NavigationLinks/>}
    renderMenu={() => <NavigationLinks/>}
    isWorkWithSeparator={true}
/>`;

const STICKY_CODE = `const [isScrolled, setIsScrolled] = useState(false);

<MainHeader
    siteLogoProps={siteLogoProps}
    isWorkWithSeparator={true}
    isScrolled={isScrolled}
    isWorkWithSticky={true}
>
    <PageContainer
        onScrollEdgeChange={setIsScrolled}
        isScrollable={true}
    >
        {content}
    </PageContainer>
</MainHeader>`;

const LIST_ITEMS = [
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10
] as const;

const getColumnStyle = ({
    spaces
}: DocsStyleTokens): CSSProperties => ({
    flexDirection: "column",
    gap: spaces.spacingSm,
    display: "flex",
    width: "100%"
});

const getLinksStyle = ({
    spaces
}: DocsStyleTokens): CSSProperties => ({
    gap: spaces.spacingXs,
    alignItems: "center",
    display: "flex"
});

const getMenuStyle = ({
    spaces
}: DocsStyleTokens): CSSProperties => ({
    flexDirection: "column",
    gap: spaces.spacingXs,
    display: "flex"
});

const getScrollStyle = ({
    shapeConfigs
}: DocsStyleTokens) => ({
    height: shapeConfigs.chartHeight.small
});

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("mainHeaderPage.logoTitle"),
        id: "logo"
    },
    {
        title: t("mainHeaderPage.menuSectionTitle"),
        id: "menu"
    },
    {
        title: t("mainHeaderPage.scrollTitle"),
        id: "scroll"
    },
    {
        title: t("mainHeaderPage.stickyTitle"),
        id: "sticky"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const MainHeaderPage = () => {
    const docsTokens = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const {
        colors
    } = NCoreUIKitTheme.useContext();

    const [
        activeLink,
        setActiveLink
    ] = useState("docs");

    const [
        isSeparatorForced,
        setIsSeparatorForced
    ] = useState(false);

    const [
        isScrolled,
        setIsScrolled
    ] = useState(false);

    const links = [
        {
            title: t("mainHeaderPage.linkDocs"),
            id: "docs"
        },
        {
            title: t("mainHeaderPage.linkComponents"),
            id: "components"
        },
        {
            title: t("mainHeaderPage.linkPricing"),
            id: "pricing"
        }
    ];

    const logoImageProps = {
        tintColor: colors.content.icon.default
    };

    const siteLogoProps: ISiteLogoProps = {
        subTitle: t("mainHeaderPage.tagline"),
        imageProps: logoImageProps,
        onPress: () => undefined,
        title: t("sample.brand"),
        isWorkWithAction: true,
        imageUrl: ncoreLogo
    };

    const handleSeparatorToggle = () => setIsSeparatorForced(!isSeparatorForced);

    const renderLinks = (spreadBehaviour: "stretch" | "free") => links.map((link) => <Button
        variant={activeLink === link.id ? "tonal" : "ghost"}
        onPress={() => setActiveLink(link.id)}
        spreadBehaviour={spreadBehaviour}
        title={link.title}
        type="neutral"
        key={link.id}
    />);

    const renderCenter = () => <div
        style={getLinksStyle(docsTokens)}
    >
        {renderLinks("free")}
    </div>;

    const renderMenu = () => <div
        style={getMenuStyle(docsTokens)}
    >
        {renderLinks("stretch")}
    </div>;

    const renderRight = () => <Button
        title={t("mainHeaderPage.signIn")}
        onPress={() => undefined}
        spreadBehaviour="free"
    />;

    const renderSwitchers = () => <Fragment>
        <Button
            accessibilityLabel={t("sample.search")}
            onPress={() => undefined}
            spreadBehaviour="free"
            icon={SearchIcon}
            variant="ghost"
            type="neutral"
        />
        <Button
            accessibilityLabel={t("mainHeaderPage.notifications")}
            onPress={() => undefined}
            spreadBehaviour="free"
            variant="ghost"
            icon={BellIcon}
            type="neutral"
        />
    </Fragment>;

    const renderFrame = (label: string, header: ReactNode) => <div
        style={getColumnStyle(docsTokens)}
        key={label}
    >
        <Text
            variant="bodySmallSize"
            color="low"
        >
            {label}
        </Text>
        <DemoFrame>
            {header}
        </DemoFrame>
    </div>;

    return <Article>
        <ComponentHeader
            lead={t("mainHeaderPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("mainHeaderPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("mainHeaderPage.overviewCaption")}
            >
                <DemoFrame>
                    <MainHeader
                        menuTitle={t("mainHeaderPage.menuTitle")}
                        isWorkWithSafeAreaView={false}
                        siteLogoProps={siteLogoProps}
                        renderCenter={renderCenter}
                        isWorkWithSeparator={true}
                        renderRight={renderRight}
                        renderMenu={renderMenu}
                    />
                </DemoFrame>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("mainHeaderPage.logoTitle")}
            id="logo"
        >
            <Paragraph>
                {t("mainHeaderPage.logoText")}
            </Paragraph>
            <Preview>
                {
                    renderFrame(t("mainHeaderPage.logoDefault"), <MainHeader
                        isWorkWithSafeAreaView={false}
                        siteLogoProps={{
                            title: t("sample.brand")
                        }}
                        isWorkWithSeparator={true}
                    />)
                }
                {
                    renderFrame("siteLogoProps · imageUrl · isWorkWithAction", <MainHeader
                        isWorkWithSafeAreaView={false}
                        siteLogoProps={siteLogoProps}
                        isWorkWithSeparator={true}
                    />)
                }
                {
                    renderFrame("renderLeft", <MainHeader
                        renderLeft={() => <Text
                            variant="titleMediumSize"
                            color="high"
                        >
                            {t("mainHeaderPage.customLeft")}
                        </Text>}
                        isWorkWithSafeAreaView={false}
                        isWorkWithSeparator={true}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("mainHeaderPage.menuSectionTitle")}
            id="menu"
        >
            <Paragraph>
                {t("mainHeaderPage.menuText")}
            </Paragraph>
            <Preview
                caption={t("mainHeaderPage.menuCaption")}
            >
                <DemoFrame>
                    <MainHeader
                        menuTitle={t("mainHeaderPage.menuTitle")}
                        renderSwitchers={renderSwitchers}
                        isWorkWithSafeAreaView={false}
                        siteLogoProps={siteLogoProps}
                        isWorkWithSeparator={true}
                        renderRight={renderRight}
                        renderMenu={renderMenu}
                    />
                </DemoFrame>
            </Preview>
        </Section>
        <Section
            title={t("mainHeaderPage.scrollTitle")}
            id="scroll"
        >
            <Paragraph>
                {t("mainHeaderPage.scrollText")}
            </Paragraph>
            <Preview
                caption={t("mainHeaderPage.scrollCaption")}
            >
                <DemoFrame>
                    <MainHeader
                        isWorkWithSeparator={isSeparatorForced ? true : undefined}
                        isWorkWithSafeAreaView={false}
                        siteLogoProps={siteLogoProps}
                        renderRight={renderRight}
                        isScrolled={isScrolled}
                    />
                    <PageContainer
                        scrollViewStyle={getScrollStyle(docsTokens)}
                        onScrollEdgeChange={setIsScrolled}
                        isWorkWithHeaderSpace={false}
                        isScrollable={true}
                    >
                        {
                            LIST_ITEMS.map((item) => <Text
                                variant="bodyMediumSize"
                                color="mid"
                                key={item}
                            >
                                {t("mainHeaderPage.scrollItem", [
                                    item
                                ])}
                            </Text>)
                        }
                    </PageContainer>
                </DemoFrame>
                <Switch
                    title={t("mainHeaderPage.scrollSwitch")}
                    onPress={handleSeparatorToggle}
                    isChecked={isSeparatorForced}
                />
            </Preview>
        </Section>
        <Section
            title={t("mainHeaderPage.stickyTitle")}
            id="sticky"
        >
            <Paragraph>
                {t("mainHeaderPage.stickyText")}
            </Paragraph>
            <CodeBlock
                code={STICKY_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("mainHeaderPage.a11yBar"),
                    t("mainHeaderPage.a11yLogo"),
                    t("mainHeaderPage.a11yMenu"),
                    t("mainHeaderPage.a11yCenter"),
                    t("mainHeaderPage.a11yOrder"),
                    t("mainHeaderPage.a11yWarn")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("mainHeaderPage.propSiteLogoProps"),
                        type: "ISiteLogoProps",
                        name: "siteLogoProps"
                    },
                    {
                        description: t("mainHeaderPage.propRenderLeft"),
                        type: "() => ReactNode",
                        name: "renderLeft"
                    },
                    {
                        description: t("mainHeaderPage.propRenderCenter"),
                        type: "() => ReactNode",
                        name: "renderCenter"
                    },
                    {
                        description: t("mainHeaderPage.propRenderRight"),
                        type: "() => ReactNode",
                        name: "renderRight"
                    },
                    {
                        description: t("mainHeaderPage.propRenderSwitchers"),
                        type: "() => ReactNode",
                        name: "renderSwitchers"
                    },
                    {
                        description: t("mainHeaderPage.propRenderMenu"),
                        type: "() => ReactNode",
                        name: "renderMenu"
                    },
                    {
                        description: t("mainHeaderPage.propMenuTitle"),
                        name: "menuTitle",
                        type: "string"
                    },
                    {
                        description: t("mainHeaderPage.propIsWorkWithSeparator"),
                        name: "isWorkWithSeparator",
                        type: "boolean"
                    },
                    {
                        description: t("mainHeaderPage.propIsScrolled"),
                        name: "isScrolled",
                        type: "boolean"
                    },
                    {
                        description: t("mainHeaderPage.propBackgroundColor"),
                        type: "keyof ContainerContentColors",
                        defaultValue: "\"default\"",
                        name: "backgroundColor"
                    },
                    {
                        description: t("mainHeaderPage.propCustomBackgroundColor"),
                        type: "keyof ProjectColorPalette",
                        name: "customBackgroundColor"
                    },
                    {
                        description: t("mainHeaderPage.propIsWorkWithSafeAreaView"),
                        name: "isWorkWithSafeAreaView",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("mainHeaderPage.propIsWorkWithSticky"),
                        name: "isWorkWithSticky",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("mainHeaderPage.propIsWorkWithStickySpace"),
                        name: "isWorkWithStickySpace",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("mainHeaderPage.propIsSetAutoHeaderSpace"),
                        name: "isSetAutoHeaderSpace",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("mainHeaderPage.propOnContentHeight"),
                        type: "(contentHeight: number | null) => void",
                        name: "onContentHeight"
                    },
                    {
                        description: t("mainHeaderPage.propIsWorkWithPortal"),
                        name: "isWorkWithPortal",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("mainHeaderPage.propPortalName"),
                        defaultValue: "\"main-header\"",
                        name: "portalName",
                        type: "string"
                    },
                    {
                        description: t("mainHeaderPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("mainHeaderPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("mainHeaderPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("mainHeaderPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default MainHeaderPage;
